import React from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { LOGO } from "@/data/media";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/listing-standard", label: "Listing Standard" },
  { to: "/pricing", label: "Pricing" },
  { to: "/faq", label: "FAQ" },
  { to: "/insights", label: "Insights" },
  { to: "/about", label: "About" },
];

export default function Header() {
  const [open, setOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);
  const location = useLocation();

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  React.useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-md border-b border-brand-gray/20"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1360px] px-5 md:px-10 h-16 md:h-[72px] flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="header-logo-link">
          <span className="relative inline-flex h-10 w-10 md:h-11 md:w-11 items-center justify-center overflow-hidden">
            <img
              src={LOGO.markLight}
              alt="LotWise Home"
              className="h-full w-full object-contain scale-[1.85]"
              draggable={false}
            />
          </span>
          <span className="text-[19px] md:text-[20px] font-bold tracking-[-0.01em] text-brand-black leading-none">
            LotWise <span className="text-brand-gray font-medium">| Home</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-[13px] font-medium tracking-tight transition-colors link-underline ${
                  isActive ? "text-brand-black" : "text-brand-gray hover:text-brand-black"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link
            to="/contact"
            data-testid="header-cta-request"
            className="inline-flex items-center rounded-full bg-brand-black text-white text-[13px] font-bold tracking-tight px-5 py-2.5 hover:bg-brand-violet transition-colors"
          >
            Request Listing Support
          </Link>
        </div>

        <button
          type="button"
          className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-md border border-brand-gray/30"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="Menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-brand-gray/20" data-testid="mobile-menu">
          <div className="px-5 py-4 flex flex-col gap-3">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                className={({ isActive }) =>
                  `text-[15px] font-medium ${isActive ? "text-brand-black" : "text-brand-gray"}`
                }
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {n.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              className="mt-2 inline-flex items-center justify-center rounded-full bg-brand-black text-white text-sm font-bold px-5 py-3"
              data-testid="mobile-cta-request"
            >
              Request Listing Support
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
